import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { nasAPI } from '../services/api';
import { Wifi, Search, RefreshCw, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import useDarkReady from '../theme/useDarkReady';

function fmtMbps(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 100) return `${v.toFixed(0)}`;
  if (v >= 10)  return `${v.toFixed(1)}`;
  return `${v.toFixed(2)}`;
}

export default function OnlineSubscribersPage() {
  useDarkReady();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [restarting, setRestarting] = useState(false);

  const load = () => {
    nasAPI.getOnlineSubscribers().then(r => {
      setRows(r.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // matches the backend's Netzur cache TTL
    return () => clearInterval(t);
  }, []);

  const q = search.trim().toLowerCase();
  const blockedCount = rows.filter(r => r.status === 'Blocked').length;
  // Blocked rows first (they need attention), otherwise keep Netzur's order.
  const filtered = rows
    .filter(r => !blockedOnly || r.status === 'Blocked')
    .filter(r => !q || (r.username || '').toLowerCase().includes(q) || (r.fullname || '').toLowerCase().includes(q))
    .sort((a, b) => (b.status === 'Blocked') - (a.status === 'Blocked'));

  // Expiry dates arrive from Netzur as 'YYYY-MM-DD ...'; show as dd Mon yyyy.
  const fmtExpiry = (d) => {
    const dt = d ? new Date(String(d).replace(' ', 'T')) : null;
    return dt && !isNaN(dt) ? dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
  };

  const toggleSelect = (username) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(username)) next.delete(username); else next.add(username);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(s => (s.size === filtered.length ? new Set() : new Set(filtered.map(r => r.username))));
  };

  const handleRestartSelected = async () => {
    const usernames = [...selected];
    if (!window.confirm(`Restart ${usernames.length} selected connection(s)? Each customer's router will disconnect and automatically reconnect.`)) return;
    setRestarting(true);
    let ok = 0, failed = 0;
    for (const username of usernames) {
      try {
        const { data } = await nasAPI.kickSession(username);
        if (data.success) ok++; else failed++;
      } catch { failed++; }
    }
    setRestarting(false);
    setSelected(new Set());
    toast[failed ? 'error' : 'success'](`Restarted ${ok}${failed ? `, ${failed} failed` : ''}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wifi size={18} color="var(--primary)" /> Online Subscribers
          <span style={{ fontSize: '.8rem', fontWeight: 500, color: 'var(--text-muted)' }}>({rows.length})</span>
          {blockedCount > 0 && (
            <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--c-dc2626)' }}>({blockedCount} blocked)</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <button className="btn btn-danger btn-sm" disabled={restarting} onClick={handleRestartSelected}>
              <Power size={13} /> {restarting ? 'Restarting…' : `Restart Selected (${selected.size})`}
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={() => { setLoading(true); load(); }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body" style={{ padding: '10px 14px' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: 10 }} />
            <input
              className="form-control"
              style={{ paddingLeft: 32, maxWidth: 320 }}
              placeholder="Search username or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button
              className={`btn btn-sm ${blockedOnly ? 'btn-danger' : 'btn-outline'}`}
              onClick={() => setBlockedOnly(v => !v)}
            >
              Blocked only ({blockedCount})
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Status</th>
                <th>Username</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Package</th>
                <th>Reason</th>
                <th>Live Speed</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No online subscribers found.</td></tr>
              )}
              {!loading && filtered.map(r => (
                <tr key={r.username} style={r.status === 'Blocked' ? { background: 'rgba(220,38,38,.06)' } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.username)}
                      onChange={() => toggleSelect(r.username)}
                    />
                  </td>
                  <td>
                    <span style={{
                      fontSize: '.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                      background: r.status === 'Blocked' ? 'var(--cb-fee2e2)' : 'var(--cb-dcfce7)',
                      color: r.status === 'Blocked' ? 'var(--c-dc2626)' : 'var(--c-16a34a)',
                    }}>
                      {r.status || 'Online'}
                    </span>
                  </td>
                  <td className="font-mono">{r.username}</td>
                  <td>
                    {r.customerId ? (
                      <Link to={`/customers/${r.customerId}`}>{r.fullname || '—'}</Link>
                    ) : (r.fullname || '—')}
                  </td>
                  <td>{r.mobile || '—'}</td>
                  <td>{r.package || '—'}</td>
                  <td>
                    {r.status !== 'Blocked' ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : r.blockReason ? (
                      <span style={{ color: 'var(--c-dc2626)', fontWeight: 600 }}>
                        {r.blockReason}
                        {r.blockReason === 'Expired' && fmtExpiry(r.expiryDate) && (
                          <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · {fmtExpiry(r.expiryDate)}</span>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--c-dc2626)' }}>Blocked (reason not reported by Netzur)</span>
                    )}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.rx !== null && r.rx !== undefined ? (
                      <>
                        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>↓{fmtMbps(r.rx)}</span>{' '}
                        <span style={{ color: 'var(--c-ea580c)', fontWeight: 600 }}>↑{fmtMbps(r.tx)}</span>{' '}
                        <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>Mbps</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
